import React from 'react';
import Layout from '@/components/layout/Layout';
import { SEO, createBreadcrumbSchema } from '@/components/seo';
import { useLanguage } from '@/contexts/LanguageContext';
import { useBlogArticle } from '@/hooks/use-blog-api';
import { useParams, Link } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ArrowLeft, ArrowRight, Calendar, Eye, User, Share2 } from 'lucide-react';
import { format } from 'date-fns';
import { motion } from 'framer-motion';
import { sanitizeHtml } from '@/lib/sanitize';
import { blogCategoryLabel } from '@/lib/blog-categories';

const BlogArticle = () => {
  const { slug } = useParams<{ slug: string }>();
  const { isRTL } = useLanguage();
  const { data: article, isLoading } = useBlogArticle(slug || '');

  const a = article as any;
  const BackIcon = isRTL ? ArrowRight : ArrowLeft;

  // Slug-derived fallbacks for prerender + crawlers; replaced with rich data
  // in the success path below.
  const slugTitle = (slug || '')
    .replace(/[-_]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
  const fallbackArticleUrl = `https://hudanlilmuttaqeen.com/blog/${slug || ''}`;

  if (isLoading) {
    return (
      <Layout>
        <SEO
          title={`${slugTitle || (isRTL ? 'المدونة' : 'Blog')} | ${isRTL ? 'مدونة هُدًى' : 'Hudan Blog'}`}
          description={isRTL ? 'مقال من مدونة هُدى للمتقين عن تعلم القرآن الكريم' : 'An article from the Hudan Lil Muttaqeen blog on learning the Holy Quran'}
          url={fallbackArticleUrl}
          type="article"
        />
        <div className="container py-12 max-w-4xl space-y-6 animate-pulse">
          <div className="h-6 bg-muted rounded w-24" />
          <div className="h-10 bg-muted rounded w-3/4" />
          <div className="h-4 bg-muted rounded w-1/3" />
          <div className="h-80 bg-muted rounded-xl" />
          <div className="space-y-3">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="h-4 bg-muted rounded" style={{ width: `${80 + Math.random() * 20}%` }} />
            ))}
          </div>
        </div>
      </Layout>
    );
  }

  if (!a) {
    return (
      <Layout>
        <SEO
          title={`${isRTL ? 'المقال غير موجود' : 'Article not found'} | ${isRTL ? 'مدونة هُدًى' : 'Hudan Blog'}`}
          description={isRTL ? 'لم نتمكن من العثور على هذا المقال' : "We couldn't find this article"}
          url={fallbackArticleUrl}
        />
        <div className="container py-20 text-center">
          <h2 className="text-2xl font-semibold text-foreground mb-2">
            {isRTL ? 'المقال غير موجود' : 'Article not found'}
          </h2>
          <Link to="/blog">
            <Button variant="outline">{isRTL ? 'العودة للمدونة' : 'Back to Blog'}</Button>
          </Link>
        </div>
      </Layout>
    );
  }

  const title = isRTL ? (a.title_ar || a.title) : a.title;
  const content = isRTL ? (a.content_ar || a.content) : a.content;
  const articleSlug = a.slug || slug || a.name;
  const articleUrl = `https://hudanlilmuttaqeen.com/blog/${articleSlug}`;
  const excerpt = (isRTL ? (a.excerpt_ar || a.excerpt) : a.excerpt) || '';
  const authorName = (isRTL ? (a.author_name_ar || a.author_name) : a.author_name) || 'Hudan Lil Muttaqeen';
  const authorAlternateName = isRTL
    ? (a.author_name_ar ? a.author_name : undefined)
    : a.author_name_ar;
  const description = (excerpt || '').toString().replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 155);

  const articleStructuredData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@context": "https://schema.org",
        "@type": "BlogPosting",
        "headline": title,
        "image": a.cover_image,
        "datePublished": a.published_date || a.published_at,
        "dateModified": a.modified || a.published_date || a.published_at,
        "inLanguage": isRTL ? "ar" : "en",
        "author": {
          "@type": "Person",
          "name": authorName,
          ...(authorAlternateName ? { "alternateName": authorAlternateName } : {}),
          "inLanguage": isRTL ? "ar" : "en",
        },
        "publisher": {
          "@type": "Organization",
          "name": "Hudan Lil Muttaqeen",
          "logo": {
            "@type": "ImageObject",
            "url": "https://hudanlilmuttaqeen.com/favicon.svg",
          },
        },
        "mainEntityOfPage": articleUrl,
      },
      createBreadcrumbSchema(
        [
          { name: isRTL ? 'الرئيسية' : 'Home', url: 'https://hudanlilmuttaqeen.com/' },
          { name: isRTL ? 'المدونة' : 'Blog', url: 'https://hudanlilmuttaqeen.com/blog' },
          { name: title, url: articleUrl },
        ],
        isRTL ? 'ar' : 'en'
      ),
    ],
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({ title, url: window.location.href });
    } else {
      navigator.clipboard.writeText(window.location.href);
    }
  };

  return (
    <Layout>
      <SEO
        title={`${title} | ${isRTL ? 'مدونة هُدًى' : 'Hudan Blog'}`}
        description={description}
        image={a.cover_image || undefined}
        type="article"
        url={articleUrl}
        structuredData={articleStructuredData}
      />
      <div className="container py-8 max-w-4xl" dir={isRTL ? 'rtl' : 'ltr'}>
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
          {/* Back link */}
          <Link to="/blog" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-6">
            <BackIcon className="h-4 w-4" />
            {isRTL ? 'العودة للمدونة' : 'Back to Blog'}
          </Link>

          {/* Meta */}
          <div className="flex items-center gap-3 mb-4">
            {a.category && <Badge variant="secondary">{blogCategoryLabel(a.category, isRTL)}</Badge>}
            {a.is_featured ? (
              <Badge className="bg-amber-500/10 text-amber-600 border-amber-200">
                {isRTL ? 'مميز' : 'Featured'}
              </Badge>
            ) : null}
          </div>

          {/* Title */}
          <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-4 leading-tight">
            {title}
          </h1>

          {/* Author & Date row */}
          <div className="flex items-center gap-4 text-sm text-muted-foreground mb-8">
            {a.author_name && (
              <span className="flex items-center gap-1.5">
                {a.author_image ? (
                  <img src={a.author_image} alt="" className="h-6 w-6 rounded-full" />
                ) : (
                  <User className="h-4 w-4" />
                )}
                {isRTL ? (a.author_name_ar || a.author_name) : a.author_name}
              </span>
            )}
            {a.published_date && (
              <span className="flex items-center gap-1.5">
                <Calendar className="h-4 w-4" />
                {format(new Date(a.published_date), 'MMMM d, yyyy')}
              </span>
            )}
            {a.views != null && (
              <span className="flex items-center gap-1.5">
                <Eye className="h-4 w-4" />
                {a.views} {isRTL ? 'مشاهدة' : 'views'}
              </span>
            )}
            <button onClick={handleShare} className="ms-auto flex items-center gap-1.5 hover:text-foreground transition-colors">
              <Share2 className="h-4 w-4" />
              {isRTL ? 'مشاركة' : 'Share'}
            </button>
          </div>

          {/* Cover image */}
          {a.cover_image && (
            <div className="rounded-xl overflow-hidden mb-8">
              <img src={a.cover_image} alt={title} className="w-full max-h-[400px] object-cover" />
            </div>
          )}

          {/* Content */}
          <div
            className="prose prose-emerald dark:prose-invert max-w-none prose-headings:text-foreground prose-p:text-muted-foreground prose-a:text-primary"
            dangerouslySetInnerHTML={{ __html: sanitizeHtml(content) }}
          />

          {/* Tags */}
          {a.tags && (
            <div className="flex flex-wrap gap-2 mt-8 pt-6 border-t">
              {a.tags.split(',').map((tag: string) => (
                <Badge key={tag.trim()} variant="outline" className="text-xs">
                  {tag.trim()}
                </Badge>
              ))}
            </div>
          )}

          {/* Back to blog */}
          <div className="mt-8 pt-6 border-t">
            <Link to="/blog">
              <Button variant="outline">
                <BackIcon className="h-4 w-4 me-2" />
                {isRTL ? 'جميع المقالات' : 'All Articles'}
              </Button>
            </Link>
          </div>
        </motion.div>
      </div>
    </Layout>
  );
};

export default BlogArticle;
